// Contrast-theme model for the SHSAT test UI ("Change the background and
// foreground color" accessibility tool). Seven official TestNav themes.
//
// The theme is applied by stamping ``data-shsat-theme="<id>"`` on the root
// SHSAT wrapper (``.digitalSHSAT``); the actual colors live in
// ``themes.css`` (CSS-variable driven). The DEFAULT theme (Black on White)
// stamps NO attribute, so none of the theme override rules apply and the
// default rendering is byte-for-byte the current UI.
//
// Persisted in localStorage keyed by testId (mirrors the
// ``shsat_start_section_<testId>`` pattern); cleared on submit/discard.

export type ShsatThemeId =
  | 'black-on-white'
  | 'black-on-cream'
  | 'black-on-light-blue'
  | 'black-on-light-magenta'
  | 'white-on-black'
  | 'yellow-on-blue'
  | 'gray-on-green';

export const DEFAULT_SHSAT_THEME: ShsatThemeId = 'black-on-white';

// Order + labels for the Contrast Settings modal radios.
export const SHSAT_THEMES: Array<{ id: ShsatThemeId; label: string }> = [
  { id: 'black-on-white', label: 'Black on White (Default)' },
  { id: 'black-on-cream', label: 'Black on Cream' },
  { id: 'black-on-light-blue', label: 'Black on Light Blue' },
  { id: 'black-on-light-magenta', label: 'Black on Light Magenta' },
  { id: 'white-on-black', label: 'White on Black' },
  { id: 'yellow-on-blue', label: 'Yellow on Blue' },
  { id: 'gray-on-green', label: 'Gray on Green' },
];

function isThemeId(value: string | null): value is ShsatThemeId {
  return !!value && SHSAT_THEMES.some((t) => t.id === value);
}

// Apply the theme to a root element by toggling ``data-shsat-theme``.
// Default (Black on White) REMOVES the attribute so zero override rules
// apply — guaranteeing the default UI is unchanged.
export function applyShsatTheme(root: HTMLElement | null, themeId: ShsatThemeId): void {
  if (!root) return;
  if (themeId === DEFAULT_SHSAT_THEME) {
    root.removeAttribute('data-shsat-theme');
  } else {
    root.setAttribute('data-shsat-theme', themeId);
  }
}

// ---- persistence (localStorage, per test) --------------------------------

function themeStorageKey(testId: string): string {
  return `shsat_theme_${testId}`;
}

export function saveShsatTheme(testId: string, themeId: ShsatThemeId): void {
  try {
    if (themeId === DEFAULT_SHSAT_THEME) {
      localStorage.removeItem(themeStorageKey(testId));
    } else {
      localStorage.setItem(themeStorageKey(testId), themeId);
    }
  } catch {
    // private browsing / quota — theme just won't persist across refresh
  }
}

export function readShsatTheme(testId: string): ShsatThemeId {
  try {
    const stored = localStorage.getItem(themeStorageKey(testId));
    return isThemeId(stored) ? stored : DEFAULT_SHSAT_THEME;
  } catch {
    return DEFAULT_SHSAT_THEME;
  }
}

export function clearShsatTheme(testId: string): void {
  try {
    localStorage.removeItem(themeStorageKey(testId));
  } catch {
    // ignore
  }
}
